import React, { useEffect, useMemo, useState } from "react";
import { ComposedChart, Area, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { PlaybackSpeedControl } from "./PlaybackSpeedControl";
import { playbackSpeedService } from "../services/playbackSpeedService";
import { useBufferAudioSync } from "../hooks/useBufferAudioSync";

interface MasterSessionVisualizerProps {
  refUrl: string;
  stuUrl: string;
  refEnvelope: number[];
  stuEnvelope: number[];
  duration: number;
  firstOnset: number;
  lagEstimateMs: number;
  playbackRatio?: number; // conservé pour compat, mais audio sync = WebAudio
  peaks: { ref: any[]; stu: any[] };
  bpm?: number;
}

const StaticSessionChart = React.memo(
  ({
    data,
    mixMode,
    totalDuration,
  }: {
    data: any[];
    mixMode: "ref" | "mix" | "stu";
    totalDuration: number;
  }) => {
    return (
      <ResponsiveContainer width="100%" height="100%" minHeight={350} debounce={100}>
        <ComposedChart data={data} margin={{ top: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradRefMSV" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00f2ff" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#00f2ff" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="gradStuMSV" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ff2e2e" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#ff2e2e" stopOpacity={0.2} />
            </linearGradient>
          </defs>

          <XAxis dataKey="time" hide type="number" domain={[0, totalDuration]} />
          <YAxis domain={[-5, 5]} hide />

          {(mixMode === "ref" || mixMode === "mix") && (
            <>
              <Area type="monotone" dataKey="refP" stroke="#00f2ff" strokeWidth={1} fill="url(#gradRefMSV)" isAnimationActive={false} baseValue={0} />
              <Area type="monotone" dataKey="refN" stroke="#00f2ff" strokeWidth={1} fill="url(#gradRefMSV)" isAnimationActive={false} baseValue={0} />
            </>
          )}

          {(mixMode === "stu" || mixMode === "mix") && (
            <>
              <Area type="monotone" dataKey="stuP" stroke="#ff2e2e" strokeWidth={2} fill="url(#gradStuMSV)" isAnimationActive={false} baseValue={0} />
              <Area type="monotone" dataKey="stuN" stroke="#ff2e2e" strokeWidth={2} fill="url(#gradStuMSV)" isAnimationActive={false} baseValue={0} />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }
);

export const MasterSessionVisualizer: React.FC<MasterSessionVisualizerProps> = React.memo(
  ({ refUrl, stuUrl, refEnvelope = [], stuEnvelope = [], duration: totalDuration, lagEstimateMs, bpm }) => {
    const hasRefData = useMemo(() => {
      return (refUrl && refUrl.length > 0) || (refEnvelope && refEnvelope.length > 0);
    }, [refUrl, refEnvelope]);

    const [mixMode, setMixMode] = useState<"ref" | "mix" | "stu">("mix");
    const [speed, setSpeed] = useState(1.0);

    useEffect(() => playbackSpeedService.subscribe(setSpeed), []);
    const handleSpeedChange = (newSpeed: number) => playbackSpeedService.setSpeed(newSpeed);

    // WebAudio master (truth)
    const { status, isPlaying, currentTime, togglePlay, setMix } = useBufferAudioSync({
      stuUrl,
      refUrl: hasRefData ? refUrl : undefined,
      refOffsetMs: -lagEstimateMs,
      playbackSpeed: speed,
    });

    // Mix mode -> gains
    useEffect(() => {
      if (!hasRefData) {
        setMix(1.0);
        return;
      }
      if (mixMode === "ref") setMix(0.0);
      else if (mixMode === "stu") setMix(1.0);
      else setMix(0.5);
    }, [mixMode, hasRefData, setMix]);

    const chartData = useMemo(() => {
      const rLen = refEnvelope?.length || 0;
      const sLen = stuEnvelope?.length || 0;
      if (rLen === 0 && sLen === 0) return [];

      const steps = 1200;
      const data: any[] = [];

      const maxRef = rLen > 0 ? refEnvelope.reduce((a, b) => Math.max(a, b), 0.001) : 0.001;
      const maxStu = sLen > 0 ? stuEnvelope.reduce((a, b) => Math.max(a, b), 0.001) : 0.001;

      const refScale = 4.0 / maxRef;
      const stuScale = 4.0 / maxStu;
      const safeTotalDuration = Math.max(0.001, totalDuration || 1);

      for (let i = 0; i < steps; i++) {
  const p = i / (steps - 1);
  const time = p * safeTotalDuration;

  const rIdx = rLen > 1 ? Math.floor(p * (rLen - 1)) : 0;
  const sIdx = sLen > 1 ? Math.floor(p * (sLen - 1)) : 0;

  const rV = rLen > 0 ? (refEnvelope[rIdx] || 0) * refScale : 0;
  const sV = sLen > 0 ? (stuEnvelope[sIdx] || 0) * stuScale : 0;

  data.push({
    time,
    refP: isFinite(rV) ? rV : 0,
    refN: isFinite(rV) ? -rV : 0,
    stuP: isFinite(sV) ? sV : 0,
    stuN: isFinite(sV) ? -sV : 0,
  });
}
      return data;
    }, [refEnvelope, stuEnvelope, totalDuration]);

    const safeDur = Math.max(0.001, totalDuration || 1);

    return (
      <div className="relative flex flex-col h-full min-h-[500px] bg-[#010507] overflow-hidden rounded-t-[40px] shadow-2xl border-t border-white/10 font-sans">
        <div className="p-8 shrink-0 flex justify-between items-center bg-black/40">
          <div className="space-y-1">
            <h3 className="text-2xl font-black text-white italic uppercase tracking-tighter">
              Audio Lab <span className="text-cyan-400">Analysis</span>
            </h3>
            <p className="text-[10px] text-slate-500 uppercase font-black tracking-[0.4em]">
              {refUrl ? "Audit Ondes Réelles" : "Audit Ondes Pulsées (Synthétique)"}
            </p>
            {status === "loading" && (
              <p className="text-[10px] text-slate-500 mt-1">Chargement audio...</p>
            )}
          </div>

          <div className="flex flex-col items-end">
            <div className="flex items-center gap-3">
              <span className="text-xl font-mono font-black text-white">{(lagEstimateMs || 0).toFixed(1)} ms</span>
              <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_#10b981]" />
            </div>
            <p className="text-[8px] text-slate-600 font-black uppercase mt-1">Calibration Matérielle Active</p>
          </div>
        </div>

        <div className="flex-1 relative overflow-hidden">
          <div className="w-full h-full relative">
            <StaticSessionChart data={chartData} mixMode={mixMode} totalDuration={safeDur} />
            <div
              className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_20px_white] z-30 transition-all duration-75"
              style={{
                left: `${((currentTime || 0) / safeDur) * 100}%`,
                transform: "translateX(-50%)",
              }}
            />
          </div>
        </div>

        <div className="h-32 bg-[#00080a] border-t border-white/10 flex items-center justify-between px-10 gap-6 shrink-0">
          <div className="flex items-center gap-6">
            <button
              onClick={togglePlay}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isPlaying ? "bg-red-600 shadow-[0_0_30px_rgba(220,38,38,0.4)]" : "bg-white text-black hover:scale-105"
              }`}
            >
              <span className="text-2xl">{isPlaying ? "⏸" : "▶"}</span>
            </button>
            <span className="text-2xl font-mono font-black text-white italic tabular-nums">{(currentTime || 0).toFixed(2)}s</span>
          </div>

          <div className="flex items-center gap-8">
            <div className="hidden md:block w-48">
              <PlaybackSpeedControl speed={speed} onSpeedChange={handleSpeedChange} />
            </div>

            <div className="flex gap-2 p-1 bg-black/40 rounded-full border border-white/5">
              {(["ref", "mix", "stu"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMixMode(m)}
                  className={`px-4 py-2 rounded-full text-[9px] font-black uppercase transition-all ${
                    mixMode === m ? "bg-white text-black shadow-lg" : "text-slate-500 hover:text-white"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
);